/**
 * Number formatting shared by the earth-science tools: scientific notation with real
 * superscripts, so 1.995e12 reads "2.00 × 10¹²" rather than "1.995e+12".
 */

import { parseNumberInput } from "@/lib/parse-number";

type NumberFormat = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

/** An integer written in superscript digits: −12 → "⁻¹²". */
export function superscript(exponent: number): string {
  const digits = String(Math.abs(Math.trunc(exponent)))
    .split("")
    .map((digit) => SUPERSCRIPT_DIGITS[Number(digit)])
    .join("");
  return exponent < 0 ? `⁻${digits}` : digits;
}

/** Mantissa (1 ≤ |m| < 10) and exponent of a nonzero finite number, rounded to `digits` decimals. */
export function splitScientific(
  value: number,
  digits: number
): { mantissa: number; exponent: number } {
  if (value === 0 || !Number.isFinite(value)) {
    return { mantissa: 0, exponent: 0 };
  }
  let exponent = Math.floor(Math.log10(Math.abs(value)));
  let mantissa = value / 10 ** exponent;
  const rounded = Number(mantissa.toFixed(digits));
  if (Math.abs(rounded) >= 10) {
    exponent += 1;
    mantissa = value / 10 ** exponent;
  }
  return { mantissa: Number(mantissa.toFixed(digits)), exponent };
}

/** "3.98 × 10¹⁹" in the reader's number format. Zero and non-finite values print plainly. */
export function formatScientific(
  fmt: NumberFormat,
  value: number,
  digits = 2
): string {
  if (value === 0 || !Number.isFinite(value)) {
    return fmt(value);
  }
  const { mantissa, exponent } = splitScientific(value, digits);
  return `${fmt(mantissa, { minimumFractionDigits: digits, maximumFractionDigits: digits })} × 10${superscript(exponent)}`;
}

function ratioDecimals(value: number): number {
  const size = Math.abs(value);
  if (size >= 100) {
    return 0;
  }
  return size >= 10 ? 1 : 2;
}

/**
 * A magnitude-sized number: grouped digits below a million, scientific above, so a
 * ratio of 31.6 reads plainly and a ratio of 2.2 × 10⁷ stays short.
 */
export function formatRatio(fmt: NumberFormat, value: number): string {
  if (!Number.isFinite(value)) {
    return fmt(value);
  }
  if (
    Math.abs(value) >= 1_000_000 ||
    (value !== 0 && Math.abs(value) < 0.001)
  ) {
    return formatScientific(fmt, value, 2);
  }
  return fmt(value, { maximumFractionDigits: ratioDecimals(value) });
}

const EXPONENT_FORM = /^(.+?)\s*(?:e|E|[x×]\s*10\s*\^?)\s*([+\-−]?\d{1,3})$/;
const UNICODE_MINUS = /−/g;

/**
 * A typed number that may carry an exponent: "4e19", "4 × 10^19", "1,5e-3". Plain numbers
 * go through `parseNumberInput`, so a decimal comma still works. Null if unreadable.
 */
export function parseScientificInput(raw: string): number | null {
  const text = raw.trim();
  const match = EXPONENT_FORM.exec(text);
  if (!match) {
    return parseNumberInput(text);
  }
  const mantissa = parseNumberInput(match[1]);
  const exponent = Number(match[2].replace(UNICODE_MINUS, "-"));
  if (mantissa === null || !Number.isInteger(exponent)) {
    return null;
  }
  const value = mantissa * 10 ** exponent;
  return Number.isFinite(value) ? value : null;
}
